// AGC: RequestPermissionsOnMount - Primes ALL permission dialogs early on app load.
// AGC: Runs once on native platforms. This is a fire-and-forget, non-blocking warm-up:
// AGC:   - Location: primes GPS permission dialog.
// AGC:   - Camera: primes CAMERA permission dialog via @capacitor/camera.
// AGC:   - Microphone: primes RECORD_AUDIO permission dialog via speech-recognition plugin.
// AGC:   - Push notifications: requests POST_NOTIFICATIONS (Android 13+), registers FCM token.
// AGC:   - If permission is already granted, does nothing.
// AGC:   - If permission is blocked, does nothing (blocked path is handled per-feature).

import { useEffect, useState } from 'react'
import { Capacitor } from '@capacitor/core'

/**
 * AGC: Invisible component -- warm-up permissions once on app start.
 * AGC: Returns push permission state so parent can show a blocked-modal if needed.
 */
export const RequestPermissionsOnMount: React.FC<{
  onPushBlocked?: () => void
  onPushNotification?: (title: string, body: string, data?: Record<string, unknown>) => void
}> = ({ onPushBlocked, onPushNotification }) => {
  const [hasRun, setHasRun] = useState(false)

  useEffect(() => {
    if (!Capacitor.isNativePlatform() || hasRun) return
    setHasRun(true)

    const warmUp = async () => {
      const { ensurePermission } = await import('../../services/permissionHelper')

      // AGC: 1. Location permission warm-up
      try {
        await ensurePermission('location')
      } catch (e) {
        console.warn('AGC: RequestPermissionsOnMount: location warm-up failed:', e)
      }

      // AGC: 2. Camera permission warm-up
      try {
        await ensurePermission('camera')
      } catch (e) {
        console.warn('AGC: RequestPermissionsOnMount: camera warm-up failed:', e)
      }

      // AGC: 3. Microphone permission warm-up
      try {
        await ensurePermission('microphone')
      } catch (e) {
        console.warn('AGC: RequestPermissionsOnMount: microphone warm-up failed:', e)
      }

      // AGC: 4. Push notification permission + FCM registration
      try {
        const { initPushNotifications, checkPushPermission } = await import(
          '../../services/pushNotificationService'
        )
        await initPushNotifications(onPushNotification)

        // AGC: If permission is denied after init attempt, notify parent
        const finalStatus = await checkPushPermission()
        if (finalStatus === 'denied' && onPushBlocked) {
          onPushBlocked()
        }
      } catch (e) {
        console.warn('AGC: RequestPermissionsOnMount: push warm-up failed:', e)
      }
    }

    warmUp()
  }, [hasRun, onPushBlocked, onPushNotification])

  return null
}
